/**
 * 作る・直すシート（#151、docs/60 §2「会社を追加」・§8。Figma ④）。会社の切替の「会社を追加」から使い始め、ほかの画面にも使う。
 *   ・スマホ版（html[data-view="phone"]）＝下から出るシート（上だけ角丸 12px・上につまみ 36×5・後ろを 40% 暗く・220ms で上がる）。
 *     PC版＝真ん中の枠（今の .dlg と同じ見た目、幅 560px まで）。分けるのは CSS だけ（.gsheet・.gsheet.is-open）
 *   ・頭は「取消｜題｜決定（作成など）」：取消は文字だけの藍、題は太字、右は太字の藍（doneDisabled なら押せない）
 *   ・中身は form（Enter でも右のボタンと同じ。変換確定の Enter は欄の guardComposingEnter が止める）
 *   ・role="dialog" aria-modal aria-labelledby。Esc・後ろを押すと取消。Tab はシートの中を回る
 *   ・開いたら中の最初の欄に focus、閉じたら開いたボタンへ focus を戻す
 *   ・スマホのキーボードが出たら、見えている範囲に合わせて持ち上げる（docs/10 §9.14 の 24b。lib/aboveKeyboard.ts＝スマホの見積のシートと同じもの）
 *   ・閉じるだけのシート（#152、docs/61 §2 ②：アカウントのメニューのスマホ版）＝closeLabel を渡す。頭は「題（左）｜×（右）」、中身は form にしない
 *   ・選ぶだけのシート（#153、docs/62 §3：社印・ロゴ。Figma ⑤）＝actions を渡す。頭なし：上のまとまり（children＝見本と題・「画像を選ぶ」・「外す」）と、
 *     分けた「取消」（cancelLabel）。iOS のアクションシートの形。PC は真ん中の小さな枠（幅 400px）。読み上げの名前は title（aria-label）
 *   ・className＝枠（.gsheet-panel）に足す class（#155 振込口座の口座のシート＝PC は 520px）。doneId＝右のボタンの id（#155 の saveBankAccount）
 *   ・lead＝題の下の1文（#185 ユーザーを招待）。bottomDone＝決定を頭の右でなく、下の 48px の主ボタン（幅いっぱい）に（#185。頭は「取消｜題」だけ）
 *   ・noCapture＝「？」の画面の写しに入れない（data-nocapture。#206 お知らせの窓）
 *   ・headEnd＝bottomDone のときの頭の右（#287 ユーザー追加の「利用人数 2／3人」のチップ）。無ければ空き
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { useAboveKeyboard } from "../lib/aboveKeyboard.js";
import { reduceMotion } from "./GroupList.jsx";

/** 閉じる動き（CSS の 220ms）が終わるまで描いておく */
const LEAVE_MS = 220;
const FOCUSABLE = 'input:not([type="hidden"]):not(:disabled),select:not(:disabled),textarea:not(:disabled),button:not(:disabled),a[href],[tabindex]:not([tabindex="-1"])';


export function Sheet({
  open,
  title,
  cancelLabel,
  doneLabel,
  doneDisabled = false,
  onCancel,
  onDone,
  children,
  testId,
  formTestId,
  doneTestId,
  cancelTestId,
  closeLabel,
  closeTestId,
  actions = false,
  className,
  doneId,
  lead,
  bottomDone = false,
  headEnd,
  noCapture = false,
}: {
  open: boolean;
  title: string;
  /** 取消（左の文字のボタン）。閉じるだけのシートでは使わない */
  cancelLabel?: string;
  doneLabel?: string;
  doneDisabled?: boolean;
  /** 取消・Esc・後ろを押した・×（閉じるだけのシート） */
  onCancel: () => void;
  onDone?: () => void;
  children: ReactNode;
  /** 閉じるだけのシート：× の読み上げの名前（渡すと頭が「題｜×」、中身は form にしない） */
  closeLabel?: string | undefined;
  closeTestId?: string | undefined;
  /** 選ぶだけのシート（頭なし・分けた取消） */
  actions?: boolean;
  /** シート（role="dialog"）の data-testid */
  testId?: string | undefined;
  /** 中の form の data-testid */
  formTestId?: string | undefined;
  doneTestId?: string | undefined;
  cancelTestId?: string | undefined;
  /** 枠に足す class */
  className?: string | undefined;
  /** 右のボタン（決定）の id */
  doneId?: string | undefined;
  /** 題の下の1文（#185） */
  lead?: ReactNode;
  /** 決定を下の主ボタンに（#185） */
  bottomDone?: boolean;
  /** bottomDone のときの頭の右（#287） */
  headEnd?: ReactNode;
  /** 画面の写しに入れない（#206） */
  noCapture?: boolean;
}) {
  const titleId = useId();
  const scrim = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  /** 描いているか（閉じる動きのあいだも描く） */
  const [shown, setShown] = useState(open);
  /** .is-open（描いた次の描画で付ける＝下から上がる動きが見える） */
  const [entered, setEntered] = useState(false);
  /** 開いたときに focus があったもの（閉じたら戻す） */
  const opener = useRef<Element | null>(null);
  /** 開いているあいだ true（後片づけのあとで戻すとき、開き直していれば戻さない＝開発の StrictMode の2回流し） */
  const liveNow = useRef(false);
  const cancel = useRef(onCancel);
  cancel.current = onCancel;

  useEffect(() => {
    if (open) {
      setShown(true);
      let r2 = 0;
      const r1 = requestAnimationFrame(() => {
        r2 = requestAnimationFrame(() => setEntered(true));
      });
      return () => {
        cancelAnimationFrame(r1);
        cancelAnimationFrame(r2);
      };
    }
    setEntered(false);
    const t = window.setTimeout(() => setShown(false), reduceMotion() ? 0 : LEAVE_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  // 開いたら（描いたら）：開いたボタンを覚えて、中の最初の欄へ。後ろは動かさない。閉じたら開いたボタンへ戻す
  const live = open && shown;
  useLayoutEffect(() => {
    if (!live) return;
    liveNow.current = true;
    const p = panel.current;
    // シートの中（開発の StrictMode の2回目＝もう中の欄にフォーカスがある）は開いた所に数えない
    if (!(p && document.activeElement && p.contains(document.activeElement))) opener.current = document.activeElement;
    const first = p?.querySelector<HTMLElement>(".gsheet-body :is(input:not([type=hidden]),select,textarea):not(:disabled)") ?? p?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus({ preventScroll: true });
    document.body.classList.add("gsheet-open");
    return () => {
      document.body.classList.remove("gsheet-open");
      liveNow.current = false;
      const o = opener.current;
      // 戻すのはコミットのあと（#206）：閉じる動きのあいだシートは DOM に残るので、ここ（コミットの途中）で戻すと、
      // React がコミットの最後に前のフォーカス（シートの中の ×）へ戻し直し、220ms 後にシートが消えると body に落ちていた。
      // フォーカスがまだシートの中か body のときだけ戻す（閉じる操作で別の所へ移したときは動かさない）
      queueMicrotask(() => {
        if (liveNow.current) return;
        const a = document.activeElement;
        if (!(a === document.body || a === null || (p && p.contains(a)))) return;
        if (o instanceof HTMLElement && o !== document.body && document.contains(o)) o.focus({ preventScroll: true });
      });
    };
  }, [live]);

  // Esc で取消
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // 上に重なった別の窓（確認コード・入れかけの確かめ）の Esc はその窓に任せる
      const t = e.target instanceof Element ? e.target : null;
      if (t && !panel.current?.contains(t) && t.closest('[role="dialog"],[role="alertdialog"]')) return;
      // 開いている「？」の吹き出しの Esc は吹き出しだけ閉じる（#156。シートの中の「？」）
      if (document.querySelector(".helppop")) return;
      e.preventDefault();
      cancel.current();
    };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [open]);

  useAboveKeyboard(scrim, shown);

  /** 後ろを押した（押し始めも後ろのとき。中で押して外で離したときは閉じない） */
  const downOnScrim = useRef(false);
  /** Tab はシートの中を回る */
  const trap = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !panel.current) return;
    const all = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!all.length) return;
    const first = all[0]!;
    const last = all[all.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!shown) return null;
  return (
    <div
      ref={scrim}
      className={"gsheet" + (entered && open ? " is-open" : "")}
      data-nocapture={noCapture ? "" : undefined}
      onPointerDown={(e) => {
        downOnScrim.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (open && downOnScrim.current && e.target === e.currentTarget) onCancel();
        downOnScrim.current = false;
      }}
    >
      <div
        ref={panel}
        className={"gsheet-panel" + (actions ? " is-acts" : "") + (className ? ` ${className}` : "")}
        // #172：スマホ版で下から出ているあいだ、通知はシートの上の端より上に出る（PC版の真ん中の枠は下に付かないので測られない）
        data-notice-above=""
        role="dialog"
        aria-modal="true"
        aria-labelledby={actions ? undefined : titleId}
        aria-label={actions ? title : undefined}
        data-testid={testId}
        onKeyDown={trap}
      >
        {actions ? null : <div className="gsheet-grab" aria-hidden="true" />}
        {actions ? (
          <>
            <div className="gsheet-acts">{children}</div>
            <button type="button" className="gsheet-actcancel" onClick={onCancel} data-testid={cancelTestId}>
              {cancelLabel}
            </button>
          </>
        ) : closeLabel ? (
          <>
            <div className="gsheet-head is-close">
              <h2 className="gsheet-ttl" id={titleId}>
                {title}
              </h2>
              <button type="button" className="gsheet-x" aria-label={closeLabel} title={closeLabel} onClick={onCancel} data-testid={closeTestId}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
            <div className="gsheet-body">{children}</div>
          </>
        ) : (
        <form
          className="gsheet-form"
          noValidate
          data-testid={formTestId}
          onSubmit={(e) => {
            e.preventDefault();
            if (open && !doneDisabled) onDone?.();
          }}
        >
          <div className="gsheet-head">
            <button type="button" className="btn text gsheet-cancel" onClick={onCancel} data-testid={cancelTestId}>
              {cancelLabel}
            </button>
            <h2 className="gsheet-ttl" id={titleId}>
              {title}
            </h2>
            {bottomDone ? (
              headEnd ? <span className="gsheet-headend">{headEnd}</span> : <span className="gsheet-headsp" aria-hidden="true" />
            ) : (
              <button type="submit" className="btn text gsheet-done" id={doneId} disabled={doneDisabled} data-testid={doneTestId}>
                {doneLabel}
              </button>
            )}
          </div>
          {lead ? <p className="gsheet-lead">{lead}</p> : null}
          <div className="gsheet-body">{children}</div>
          {bottomDone ? (
            <div className="gsheet-foot">
              <button type="submit" className="btn primary gsheet-bottom" id={doneId} disabled={doneDisabled} data-testid={doneTestId}>
                {doneLabel}
              </button>
            </div>
          ) : null}
        </form>
        )}
      </div>
    </div>
  );
}
